<template>
  <div class="workspace-documents-read-only-tab">
    <div class="workspace-documents-read-only-tab__actions">
      <vf-button
        v-if="canAddDocumentsToWorkspaces"
        data-testid="add-for-viewing"
        :variant="ButtonVariant.SECONDARY"
        @click="emitAddForViewing"
      >
        {{ t('workspaceDocuments.addForViewing') }}
      </vf-button>
    </div>
    <table-wrapper
      v-model:filters="filters"
      v-model:page-options="pageOptions"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :loading="isLoading"
      :items="documents"
      :mode="TableMode.LAZY"
      row-clickable
      :table-style="{
        'word-break': 'break-all',
      }"
      @row-click="goToWorkspaceDocumentDetailView"
    >
      <template #cell(renditionFilePresent)="{ data }">
        <font-awesome-icon v-if="data?.renditionFilePresent" :icon="faCheck" />
        <font-awesome-icon v-else :icon="faXmark" />
      </template>
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { useNavigator } from '@/composables/useNavigator';
  import { useRoles } from '@/composables/useRoles';
  import { useToast } from '@/composables/useToast';
  import { endpointsList, formatDateForDisplay } from '@/helpers';
  import axios, { AxiosResponse } from 'axios';
  import {
    IWorkspaceReadOnlyDocumentsListFilters,
    IWorkspaceReadOnlyDocumentsListItemDto,
    IWorkspaceReadOnlyDocumentsListPageDto,
    IWorkspaceReadOnlyDocumentsListPageOptionsDto,
    Order,
  } from 'types';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { READ_ONLY_TABLE_NAMESPACE } from './constants';
  import { WorkspaceDocumentsEmbeddedTab } from './WorkspaceDocuments.types';
  import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
  import { faFileCad } from '@fortawesome/pro-solid-svg-icons';
  import { ButtonVariant } from '../VfButton/VfButton.types';

  const props = defineProps<{
    workspaceId: string;
  }>();

  const emit = defineEmits<{
    (e: 'add-for-viewing'): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const { goToWorkspaceDocumentDetailView: navigateToWorkspaceDocument } =
    useNavigator();
  const { canManageWorkspaceDocuments: canAddDocumentsToWorkspaces } = useRoles();

  const documents = ref<IWorkspaceReadOnlyDocumentsListItemDto[]>([]);

  const { isLoading, pageOptions, filters, filterParams } = useTableWrapper<
    IWorkspaceReadOnlyDocumentsListItemDto,
    IWorkspaceReadOnlyDocumentsListPageOptionsDto
  >(
    TableMode.LAZY,
    'lastModified',
    getReadOnlyWorkspaceDocuments,
    Order.DESC,
    true,
    READ_ONLY_TABLE_NAMESPACE,
  );

  const tableFields = computed(() =>
    new TableFieldsBuilder<IWorkspaceReadOnlyDocumentsListItemDto>({
      labelTranslationKey: 'workspaceDocuments',
      sortable: true,
    })
      .addField('title', {
        sortable: true,
        label: t('workspaceDocuments.columnTitle'),
        width: '42%',
      })
      .addField('renditionFilePresent', {
        width: '10%',
        icon: faFileCad,
        sortable: false,
      })
      .addField('version', {
        sortable: true,
        label: t('workspaceDocuments.version'),
        width: '21%',
      })
      .addField('lastModified', {
        sortable: true,
        label: t('workspaceDocuments.lastModified'),
        width: '27%',
        valueGetter: (item) =>
          item.lastModified
            ? formatDateForDisplay(new Date(item.lastModified))
            : '',
      })
      .build(),
  );

  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<
      IWorkspaceReadOnlyDocumentsListItemDto,
      IWorkspaceReadOnlyDocumentsListFilters
    >({
      translationKey: 'workspaceDocuments',
    })
      .addInputFilter('title', {
        width: '200px',
        simpleLabel: true,
      })
      .addInputFilter('version', {
        width: '120px',
        simpleLabel: true,
      })
      .addDateRangeFilter('lastModified', {
        dateFromKey: 'lastModifiedFrom',
        dateToKey: 'lastModifiedTo',
        simpleLabel: true,
      })
      .build(),
  );

  function emitAddForViewing(): void {
    emit('add-for-viewing');
  }

  async function getReadOnlyWorkspaceDocuments(): Promise<void> {
    try {
      const response: AxiosResponse<IWorkspaceReadOnlyDocumentsListPageDto> =
        await axios.get(
          endpointsList.workspaceUploads.getReadOnlyWorkspaceDocuments(
            props.workspaceId,
            {
              order: pageOptions.value.sortOrder,
              page: pageOptions.value.page,
              sortBy: pageOptions.value.sortBy,
              take: pageOptions.value.perPage,
              ...filterParams.value,
            },
          ),
        );
      documents.value = response.data.data;
      pageOptions.value.rows = response.data.meta.itemCount;
    } catch {
      createErrorToast(t('workspaceDocuments.errors.errorGettingDocuments'));
    }
  }

  function goToWorkspaceDocumentDetailView(
    data: IWorkspaceReadOnlyDocumentsListItemDto,
  ): void {
    navigateToWorkspaceDocument(
      props.workspaceId,
      data.id,
      WorkspaceDocumentsEmbeddedTab.READ_ONLY,
    );
  }

  defineExpose({
    refreshDocuments: getReadOnlyWorkspaceDocuments,
  });
</script>

<style scoped lang="scss">
  .workspace-documents-read-only-tab__actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
  }
</style>
